import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Helmet } from "react-helmet-async";

const PROD_DOMAIN = "https://www.thesecretseller.co.uk";

export default function LandingSellEmptyPropertyPage() {
  return (
    <main data-testid="landing-sell-empty" className="pt-24">
      <Helmet>
        <title>How to Sell an Empty Property | Virtual Staging UK | The Secret Seller</title>
        <meta name="description" content="Empty properties struggle to sell. Learn how virtual staging transforms empty rooms into buyer-ready spaces, increases enquiries and helps achieve faster sales." />
        <link rel="canonical" href={`${PROD_DOMAIN}/how-to-sell-empty-property`} />
      </Helmet>

      <section className="py-20 lg:py-28 bg-[#FAFAF8]">
        <div className="max-w-3xl mx-auto px-8 lg:px-16 text-center">
          <p className="uppercase text-[11px] tracking-[0.3em] text-[#758C7B] mb-4 font-medium">Property Marketing</p>
          <h1 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-[2.8rem] font-extralight text-[#0D2D54] leading-[1.1] tracking-[-0.01em] mb-8">
            How to sell an empty property
          </h1>
          <p className="text-[15px] text-[#6B7280] leading-[1.8]">
            Empty properties sit on the market longer, attract fewer viewings and often sell for less. Here's why — and what you can do about it.
          </p>
        </div>
      </section>

      <section className="py-24 lg:py-32">
        <div className="max-w-3xl mx-auto px-8 lg:px-16 space-y-12">
          <div>
            <h2 className="font-['Outfit'] text-xl lg:text-2xl font-extralight text-[#0D2D54] mb-4">Why empty properties struggle to sell</h2>
            <p className="text-[15px] text-[#6B7280] leading-[1.8] mb-4">When buyers browse Rightmove or Zoopla, they're making split-second decisions about which properties to click on. Empty rooms look cold, small and forgettable. Research shows that staged homes sell up to 73% faster than their empty equivalents.</p>
            <p className="text-[15px] text-[#6B7280] leading-[1.8]">The problem isn't the property itself — it's the presentation. Buyers need to see potential, and an empty room doesn't give them that.</p>
          </div>

          <div>
            <h2 className="font-['Outfit'] text-xl lg:text-2xl font-extralight text-[#0D2D54] mb-4">The importance of first impressions</h2>
            <p className="text-[15px] text-[#6B7280] leading-[1.8]">95% of buyers start their search online. Your listing photos are the first — and sometimes only — chance to make an impression. Properties with professionally presented imagery receive significantly more clicks, more viewings and stronger offers.</p>
          </div>

          <div>
            <h2 className="font-['Outfit'] text-xl lg:text-2xl font-extralight text-[#0D2D54] mb-4">How virtual staging helps</h2>
            <p className="text-[15px] text-[#6B7280] leading-[1.8] mb-4">Virtual staging digitally furnishes your empty room photos with realistic furniture, decor and styling. The result is photorealistic imagery that helps buyers visualise a property as a home — without the cost, time or logistics of physical staging.</p>
            <ul className="space-y-3 text-[15px] text-[#6B7280]">
              {["From £55 per image — a fraction of physical staging costs", "Delivered in as little as 48 hours", "Multiple styles available to suit any property type", "Proven to increase portal clicks and viewing requests"].map((item, i) => (
                <li key={i} className="flex items-start gap-3"><Check size={16} className="text-[#758C7B] mt-0.5 flex-shrink-0" />{item}</li>
              ))}
            </ul>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <img src="https://customer-assets.emergentagent.com/job_virtual-stage-uk/artifacts/v5bxzux5_8568101-interior10.jpg" alt="Empty room before staging" className="w-full h-auto" />
              <p className="text-[11px] uppercase tracking-[0.2em] text-[#6B7280] mt-3 text-center">Before</p>
            </div>
            <div>
              <img src="https://customer-assets.emergentagent.com/job_virtual-stage-uk/artifacts/yi5zide3_bed1.jpg" alt="Staged room" className="w-full h-auto" />
              <p className="text-[11px] uppercase tracking-[0.2em] text-[#0D2D54] mt-3 text-center">After</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-[#0D2D54]">
        <div className="max-w-3xl mx-auto px-8 lg:px-16 text-center">
          <h2 className="font-['Outfit'] text-2xl lg:text-[2rem] font-extralight text-white mb-6">Ready to transform your empty property?</h2>
          <p className="text-[15px] text-white/50 mb-10">Send us your photos. We'll return beautifully staged images within 48 hours.</p>
          <Link to="/order" className="inline-flex items-center gap-3 bg-white text-[#0D2D54] px-10 py-4 text-[13px] font-medium uppercase tracking-[0.15em] hover:bg-white/90 transition-all">
            Send Us Your Photos <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </main>
  );
}
